// ViewSalesInvoiceDetails.jsx - Complete file with online order support and print styles
import React, { useState, useEffect } from "react";
import { useNavigationEvent } from "navigation-react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPrint,
  faArrowLeft,
  faTimesCircle,
  faSpinner,
  faMessage,
  faEnvelope,
  faSms,
  faPlus,
  faBook,
  faClose,
  faHistory,
  faReceipt,
  faGlobe,
  faStore,
  faTruck,
  faCheck,
  faClock,
  faBan,
} from "@fortawesome/free-solid-svg-icons";
import useSWR from "swr";
import mainLogo from "./images/mainlogo.png";
import DashboardContenLeftMenus from "./DashboardContenLeftMenus";
import DashboardContentHeader from "./DashboardContentHeader";
import DashboardContentFooter from "./DashboardContentFooter";
import TopActionMenus from "./TopActionMenus";
import SecureLS from "secure-ls";
import { pdf } from "@react-pdf/renderer";
import PaymentModal from "./PaymentModal";
import PaymentHistory from "./PaymentHistory";
import InvoiceReceiptPdf from "./InvoiceReceiptPdf";

const ls = new SecureLS({ encodingType: "aes" });

// Reuse the fetcher and getCsrfToken functions from ViewSalesRegister
const getCsrfToken = async () => {
  try {
    const response = await fetch(
      `${process.env.REACT_APP_BASE_URL}/session/token`,
      {
        credentials: "include",
        headers: {
          Accept: "text/plain",
        },
      },
    );

    if (response.ok) {
      const token = await response.text();
      ls.set("usercsrf_token", token);
      return token;
    }

    const storedToken = ls.get("usercsrf_token");
    if (storedToken) {
      return storedToken;
    }

    return null;
  } catch (error) {
    console.error("Error getting CSRF token:", error);
    return ls.get("usercsrf_token") || null;
  }
};

const fetcher = async (url) => {
  try {
    const csrfToken = await getCsrfToken();

    const headers = {
      Accept: "application/vnd.api+json",
      "Content-Type": "application/vnd.api+json",
    };

    if (csrfToken) {
      headers["X-CSRF-Token"] = csrfToken;
    }

    const response = await fetch(url, {
      credentials: "include",
      headers: headers,
    });

    if (!response.ok) {
      throw new Error(`API request failed with status ${response.status}`);
    }

    return response.json();
  } catch (error) {
    console.error("Fetch error:", error);
    throw error;
  }
};

export default function ViewSalesInvoiceDetails() {
  const { stateNavigator, data } = useNavigationEvent();
  const saleId = data?.salesId;

  const [invoiceData, setInvoiceData] = useState(null);
  const [customerData, setCustomerData] = useState(null);
  const [cashierData, setCashierData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [showPaymentAddModal, setShowPaymentAddModal] = useState(false);
  const [showPaymentHistoryModal, setShowPaymentHistoryModal] = useState(false);
  const [isPrinting, setIsPrinting] = useState(false);
  const [deliveryStatus, setDeliveryStatus] = useState("pending");
  const [updatingStatus, setUpdatingStatus] = useState(false);

  // Fetch invoice data with included relationships
  const { data: invoiceResponse, error: fetchError } = useSWR(
    saleId
      ? `${process.env.REACT_APP_SALES_ORDER_API}/${saleId}?include=field_cashier,field_customer&fields[node--sales_order]=field_order_number,field_total_amount,field_total_paid,field_discount,field_payment_type,field_online_order,field_delivery_status,field_delivery_address,created,field_sales_order_notes,drupal_internal__nid`
      : null,
    fetcher,
    {
      revalidateOnFocus: false,
      shouldRetryOnError: false,
    },
  );
  console.log("Invoice Response:", invoiceResponse);

  const { data: invoiceorderedItems, mutate: refreshInvoice } = useSWR(
    `${process.env.REACT_APP_POS_SINGLE_ORDER_API}/${saleId}`,
    fetcher,
    { revalidateOnFocus: false },
  );

  console.log("Order Items Single Data:", invoiceorderedItems);

  const orderedproducts = invoiceorderedItems?.order;

  useEffect(() => {
    if (invoiceResponse?.data) {
      const order = invoiceResponse.data;
      setInvoiceData(order);

      // Get delivery status
      if (order.attributes.field_delivery_status) {
        setDeliveryStatus(order.attributes.field_delivery_status);
      }

      // Extract included data
      if (invoiceResponse.included) {
        // Find customer data
        const customer = invoiceResponse.included.find(
          (item) => item.type === "node--customer",
        );
        if (customer) {
          setCustomerData({
            name: customer.attributes.title || "N/A",
            email: customer.attributes.field_customer_email || "",
            phone: customer.attributes.field_phone_number || "",
            address: customer.attributes.field_customer_address || "N/A",
          });
        }

        // Find cashier data
        const cashier = invoiceResponse.included.find(
          (item) => item.type === "user--user",
        );
        if (cashier) {
          setCashierData({
            name: cashier.attributes.name || "N/A",
            email: cashier.attributes.mail || "",
          });
        }
      }

      setLoading(false);
    }

    if (fetchError) {
      setError(fetchError.message);
      setLoading(false);
    }
  }, [invoiceResponse, fetchError]);

  // ============ SAFE VALUE EXTRACTORS ============
  const safeString = (value) => {
    if (value === null || value === undefined) return "";
    if (typeof value === 'string') return value;
    if (typeof value === 'number') return String(value);
    if (typeof value === 'object') {
      if (value.value !== undefined) return String(value.value);
      if (value.format !== undefined) return String(value.format);
      if (value.processed !== undefined) return String(value.processed);
      return JSON.stringify(value);
    }
    return String(value);
  };

  const safeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    if (typeof value === 'number') return value;
    if (typeof value === 'string') return parseFloat(value) || 0;
    if (typeof value === 'object') {
      if (value.value !== undefined) return parseFloat(value.value) || 0;
      if (value.format !== undefined) return parseFloat(value.format) || 0;
    }
    return 0;
  };

  const safeDate = (value) => {
    if (!value) return null;
    try {
      if (typeof value === 'string') return new Date(value);
      if (typeof value === 'number') return new Date(value);
      if (typeof value === 'object') {
        const val = value.value || value.format || value.processed || null;
        if (val) return new Date(val);
      }
      return null;
    } catch (e) {
      return null;
    }
  };

  const formatDate = (value) => {
    const date = safeDate(value);
    if (!date || isNaN(date.getTime())) return "N/A";
    return date.toLocaleDateString("en-GH", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
    });
  };

  const formatDateTime = (value) => {
    const date = safeDate(value);
    if (!date || isNaN(date.getTime())) return "N/A";
    return date.toLocaleString("en-GH", {
      year: "numeric",
      month: "numeric",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  // Update delivery status
  const updateDeliveryStatus = async (newStatus) => {
    if (!saleId) return;
    
    setUpdatingStatus(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/orders/delivery-status`,
        {
          method: "POST",
          credentials: "include",
          headers: {
            "Content-Type": "application/json",
            Accept: "application/json",
          },
          body: JSON.stringify({
            order_uuid: saleId,
            status: newStatus,
          }),
        }
      );

      if (response.ok) {
        setDeliveryStatus(newStatus);
        refreshInvoice();
        alert(`Delivery status updated to: ${newStatus}`);
      } else {
        throw new Error("Failed to update delivery status");
      }
    } catch (error) {
      console.error("Error updating delivery status:", error);
      alert("Failed to update delivery status. Please try again.");
    } finally {
      setUpdatingStatus(false);
    }
  };

  const getDeliveryStatusBadge = (status) => {
    const statusMap = {
      pending: { color: "#ff9800", bg: "#fff3e0", icon: faClock, label: "Pending" },
      processing: { color: "#1976d2", bg: "#e3f2fd", icon: faSpinner, label: "Processing" },
      shipped: { color: "#9c27b0", bg: "#f3e5f5", icon: faTruck, label: "Shipped" },
      delivered: { color: "#388e3c", bg: "#e8f5e9", icon: faCheck, label: "Delivered" },
      cancelled: { color: "#d32f2f", bg: "#ffebee", icon: faBan, label: "Cancelled" },
    };
    return statusMap[status] || statusMap.pending;
  };

  const deliveryStatusOptions = [
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "shipped", label: "Shipped" },
    { value: "delivered", label: "Delivered" },
    { value: "cancelled", label: "Cancelled" },
  ];

  // Function to print A4/PDF receipt
  const handlePrint = () => {
    const printContent = document.getElementById("invoice-print-content");
    const originalContent = document.body.innerHTML;

    if (printContent) {
      document.body.innerHTML = printContent.innerHTML;
      window.print();
      document.body.innerHTML = originalContent;
      window.location.reload();
    } else {
      window.print();
    }
  };

  // Function to print thermal receipt
  const printThermalReceiptDirectly = async () => {
    if (isPrinting) return;

    setIsPrinting(true);

    try {
      const items = invoiceorderedItems?.order?.items || [];
      const discountFromApi = safeNumber(invoiceData?.attributes?.field_discount);

      const itemsSubtotal = items.reduce((sum, item) => {
        const qty = safeNumber(item.quantity);
        const price = safeNumber(item.unit_price);
        return sum + qty * price;
      }, 0);

      const netTotal = Math.max(0, itemsSubtotal - discountFromApi);
      const totalPaidAmount = safeNumber(invoiceorderedItems?.order?.total_paid);
      const remainingBalanceAmount = safeNumber(invoiceorderedItems?.order?.remaining_balance);

      const pdfBlob = await pdf(
        <InvoiceReceiptPdf
          shopEmail="info@prosepservices.com"
          shopPhone="0244235128 / 0302790300"
          invoiceData={invoiceData}
          customerData={customerData}
          items={items}
          total={netTotal}
          discount={discountFromApi}
          totalPaid={totalPaidAmount}
          remainingBalance={remainingBalanceAmount}
        />,
      ).toBlob();

      const pdfUrl = URL.createObjectURL(pdfBlob);

      const iframe = document.createElement("iframe");
      iframe.style.position = "absolute";
      iframe.style.width = "0";
      iframe.style.height = "0";
      iframe.style.border = "none";
      iframe.style.display = "none";
      document.body.appendChild(iframe);

      iframe.src = pdfUrl;

      iframe.onload = () => {
        setTimeout(() => {
          try {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();

            const cleanup = () => {
              setTimeout(() => {
                document.body.removeChild(iframe);
                URL.revokeObjectURL(pdfUrl);
                setIsPrinting(false);
              }, 1000);
            };

            iframe.contentWindow?.addEventListener("afterprint", cleanup);
            setTimeout(cleanup, 10000);
          } catch (printError) {
            console.error("Print error:", printError);
            document.body.removeChild(iframe);
            URL.revokeObjectURL(pdfUrl);
            alert("Failed to print. Please try the A4/PDF Invoice option.");
            setIsPrinting(false);
          }
        }, 1000);
      };

      iframe.onerror = (error) => {
        console.error("Iframe load error:", error);
        document.body.removeChild(iframe);
        URL.revokeObjectURL(pdfUrl);
        alert(
          "Failed to load receipt for printing. Please try the A4/PDF Invoice option.",
        );
        setIsPrinting(false);
      };
    } catch (error) {
      console.error("PDF generation error:", error);
      alert(`Failed to generate receipt: ${error.message || "Unknown error"}`);
      setIsPrinting(false);
    }
  };

  const handleBack = () => {
    stateNavigator.navigate("viewsalesregister");
  };

  // Handle payment success
  const handlePaymentSuccess = (result) => {
    alert(`Payment of GHC${result.amount_paid} added successfully!`);
    setTimeout(() => {
      window.location.reload();
    }, 1500);
  };

  // Add Payment Button Click Handler
  const handleAddPayment = () => {
    setShowPaymentAddModal(true);
  };

  const calculateTotals = () => {
    const discountFromApi = safeNumber(invoiceData?.attributes?.field_discount);

    if (invoiceorderedItems?.order) {
      const itemsSubtotal =
        invoiceorderedItems.order.items?.reduce((sum, item) => {
          const qty = safeNumber(item.quantity);
          const price = safeNumber(item.unit_price);
          return sum + qty * price;
        }, 0) || 0;

      const discount = discountFromApi;
      const netTotal = Math.max(0, itemsSubtotal - discount);

      return {
        subtotal: itemsSubtotal,
        discount: discount,
        tax: 0,
        total: netTotal,
        totalPaid: safeNumber(invoiceorderedItems.order.total_paid),
        remainingBalance: safeNumber(invoiceorderedItems.order.remaining_balance),
        paymentStatus: invoiceorderedItems.order.payment_status || "unknown",
      };
    }

    if (!orderedproducts?.items?.length) {
      return {
        subtotal: 0,
        discount: 0,
        tax: 0,
        total: 0,
        totalPaid: 0,
        remainingBalance: 0,
        paymentStatus: "unknown",
      };
    }

    const subtotal = orderedproducts?.items?.reduce((sum, item) => {
      const qty = safeNumber(item.quantity);
      const price = safeNumber(item.unit_price);
      return sum + qty * price;
    }, 0);

    const discount = discountFromApi;
    const netTotal = Math.max(0, subtotal - discount);

    return {
      subtotal,
      discount,
      tax: 0,
      total: netTotal,
      totalPaid: netTotal,
      remainingBalance: 0,
      paymentStatus: "paid",
    };
  };
  const { subtotal, discount, tax, total, totalPaid, remainingBalance } =
    calculateTotals();

  const isOnlineOrder = safeString(invoiceData?.attributes?.field_online_order) === "1" ||
    safeString(invoiceData?.attributes?.field_online_order) === "true" ||
    invoiceData?.attributes?.field_online_order === true;

  const formatGhanaPhoneNumber = (phone) => {
    const phoneStr = safeString(phone);
    if (!phoneStr) return "N/A";
    let digits = phoneStr.replace(/\D/g, "");
    if (digits.startsWith("0")) {
      digits = digits.substring(1);
    }
    if (!digits.startsWith("233")) {
      digits = `233${digits}`;
    }
    if (digits.length === 12) {
      return `${digits.slice(0, 3)} ${digits.slice(3, 5)} ${digits.slice(
        5,
        8,
      )} ${digits.slice(8)}`;
    }
    return phoneStr;
  };

  const ghanaPhoneForWhatsApp = (phone) =>
    formatGhanaPhoneNumber(phone).replace(/\s/g, "");

  // Send WhatsApp Invoice
  const sendInvoiceViaWhatsApp = () => {
    if (!orderedproducts?.items?.length) {
      alert("No invoice items available");
      return;
    }

    const phone = `${ghanaPhoneForWhatsApp(customerData?.phone || "")}`;

    let message = `*PROSEP SERVICES LIMITED - ${isOnlineOrder ? 'ONLINE ORDER' : 'RECEIPT'}*\n`;

    message += `Invoice #: ${safeString(invoiceData?.attributes?.field_order_number)}\n`;
    message += `Date: ${formatDateTime(invoiceData?.attributes?.created)}\n`;

    if (isOnlineOrder) {
      message += `Order Type: 🌐 Online Order\n`;
      message += `Delivery Status: ${deliveryStatus.toUpperCase()}\n`;
      if (invoiceData?.attributes?.field_delivery_address) {
        message += `Delivery Address: ${safeString(invoiceData.attributes.field_delivery_address)}\n`;
      }
    }

    message += `\nHi ${
      customerData?.name || "Customer"
    }!, Thank you for shopping with us\n\n`;
    message += `*Items:*\n`;

    invoiceorderedItems?.order?.items?.forEach((item) => {
      message += `• ${safeString(item.product_title)}\n`;
      message += `  ${safeNumber(item.quantity)} x GHC${safeNumber(item.unit_price).toFixed(
        2,
      )} = GHC${safeNumber(item.line_total).toFixed(2)}\n\n`;
    });

    message += `*Payment Summary:*\n`;
    message += `Items Subtotal: GHC${subtotal.toFixed(2)}\n`;

    if (discount > 0) {
      message += `Discount: - GHC${discount.toFixed(2)}\n`;
    }

    message += `Total Amount: GHC${total.toFixed(2)}\n`;
    message += `Total Paid: GHC${totalPaid.toFixed(2)}\n`;

    if (isOnlineOrder && remainingBalance > 0) {
      message += `⏳ Payment Status: Pending (Balance: GHC${remainingBalance.toFixed(2)})\n`;
      message += `📱 Payment method: Cash on Delivery\n`;
    }

    message += `Balance Due: GHC${remainingBalance.toFixed(2)}\n\n`;

    if (isOnlineOrder) {
      message += `📦 Your order has been confirmed and will be processed shortly.\n`;
      message += `🚚 Delivery will be arranged based on your delivery address.\n`;
      message += `📊 Current Delivery Status: ${deliveryStatus.toUpperCase()}\n\n`;
    }

    message += `View full receipt: ${process.env.REACT_APP_BASE_URL}/invoice/${saleId}\n\n`;
    message += `Thank you for your business 🙏\n`;
    message += `We hope to see you again soon! 🙏\n\n`;

    const url = `https://wa.me/${phone}?text=${encodeURIComponent(message)}`;

    window.open(url, "_blank");
  };

  // Send SMS Invoice
  const handleSMS = async () => {
    try {
      let message = `Hi ${customerData?.name || "Customer"}, `;

      if (isOnlineOrder) {
        message += `Thank you for your ONLINE ORDER. `;
      } else {
        message += `Thank you for shopping at Prosep Services. `;
      }

      message += `Receipt# ${safeString(invoiceData?.attributes?.field_order_number)} - `;
      message += `Items Subtotal: GHC${subtotal.toFixed(2)}, `;

      if (discount > 0) {
        message += `Discount: - GHC${discount.toFixed(2)}, `;
      }

      message += `Total: GHC${total.toFixed(2)}. `;
      message += `Total Paid: GHC${totalPaid.toFixed(2)}. `;

      if (isOnlineOrder && remainingBalance > 0) {
        message += `Payment: Cash on Delivery (Balance: GHC${remainingBalance.toFixed(2)}). `;
      } else {
        message += `Remaining Balance: GHC${remainingBalance.toFixed(2)}. `;
      }

      if (isOnlineOrder) {
        message += `Your order has been confirmed. Delivery Status: ${deliveryStatus.toUpperCase()}. `;
        if (invoiceData?.attributes?.field_delivery_address) {
          message += `Delivery Address: ${safeString(invoiceData.attributes.field_delivery_address)}. `;
        }
      }

      message += `View your full receipt: ${process.env.REACT_APP_BASE_URL}/invoice/${saleId}`;

      const response = await fetch(
        `${process.env.REACT_APP_BASE_URL}/pos/sms/send`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            phone: ghanaPhoneForWhatsApp(customerData?.phone || ""),
            message: message,
          }),
        },
      );

      if (!response.ok) {
        throw new Error("SMS failed");
      }

      alert("SMS sent successfully");
    } catch (err) {
      console.error(err);
      alert("Failed to send SMS");
    }
  };

  // Send EMAIL Invoice
  const handleEmail = () => {
    alert(
      "Email Invoice will be available soon. Use the other options for now. Thank you.",
    );
  };

  if (loading) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <TopActionMenus
              AddTitle="Invoice Details"
              backIcon={faArrowLeft}
              backLable="Back to Register"
            />
            <div style={styles.loadingContainer}>
              <FontAwesomeIcon
                icon={faSpinner}
                spin
                size="3x"
                style={styles.loadingSpinner}
              />
              <p style={styles.loadingText}>Loading invoice details...</p>
            </div>
          </div>
        </div>
        {/* <DashboardContentFooter /> */}
      </div>
    );
  }

  if (error || !invoiceData) {
    return (
      <div className="dashboard-container">
        <DashboardContentHeader />
        <div className="content-wrapper">
          <DashboardContenLeftMenus />
          <div style={styles.container}>
            <TopActionMenus
              AddTitle="Invoice Details"
              buttonLabel="Back to Register"
              icon={faArrowLeft}
              onClick={handleBack}
            />
            <div style={styles.errorContainer}>
              <FontAwesomeIcon icon={faTimesCircle} style={styles.errorIcon} />
              <h3>Invoice Not Found</h3>
              <p>{error || "The requested invoice could not be found."}</p>
              <button style={styles.backButton} onClick={handleBack}>
                <FontAwesomeIcon icon={faArrowLeft} /> Back to Sales Register
              </button>
            </div>
          </div>
        </div>
        {/* <DashboardContentFooter /> */}
      </div>
    );
  }

  const statusBadge = getDeliveryStatusBadge(deliveryStatus);

  return (
    <div className="dashboard-container">
      <DashboardContentHeader />
      <div className="content-wrapper">
        <DashboardContenLeftMenus />
        <div style={styles.container}>
          <TopActionMenus
            AddTitle="Invoice Details"
            buttonLabel="Back to Register"
            icon={faArrowLeft}
            onClick={handleBack}
          />

          {/* PRINTABLE CONTENT */}
          <div id="invoice-print-content" style={styles.printContent}>
            {/* INVOICE HEADER - Hidden on screen, visible on print */}
            <div style={styles.invoiceHeader} className="print-only-header">
              <div style={styles.companyInfo}>
                <div style={styles.companyLogo}>
                  <img
                    src={mainLogo}
                    alt="Prosep Services Logo"
                    style={{ width: "100%", height: "100%" }}
                  />
                </div>
                <div style={styles.companyContact}>
                  <p style={styles.companyContactText}>
                    <span>Postal Address:</span> P.O. Box 6913, Accra-North
                  </p>
                  <p style={styles.companyContactText}>
                    <span>Tel:</span> +233 (0) 302 790300 / +233 (0) 261 128962
                  </p>
                  <p style={styles.companyContactText}>
                    info@prosepservices.com | www.prosepservices.com
                  </p>
                </div>
              </div>

              <div style={styles.invoiceTitleContainer}>
                <div style={styles.invoiceMeta}>
                  <h2 style={styles.invoiceTitle}>INVOICE</h2>
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Invoice #:</span>
                    <span style={styles.metaValue}>
                      {safeString(invoiceData.attributes.field_order_number)}
                    </span>
                  </div>
                  <div style={styles.metaItem}>
                    <span style={styles.metaLabel}>Date:</span>
                    <span style={styles.metaValue}>
                      {formatDate(invoiceData.attributes.created)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* ONLINE ORDER INDICATOR - Hidden when printing */}
            {isOnlineOrder && (
              <div style={styles.onlineOrderBanner} className="online-order-banner">
                <FontAwesomeIcon icon={faGlobe} style={styles.onlineOrderIcon} />
                <span style={styles.onlineOrderText}>🌐 ONLINE ORDER</span>
                <span style={styles.onlineOrderSubtext}>
                  This order was placed online by the customer.
                  {remainingBalance > 0 && ' Payment is pending (Cash on Delivery).'}
                </span>
              </div>
            )}

            {/* CUSTOMER AND PAYMENT INFO */}
            <div style={styles.infoGrid}>
              <div style={styles.infoCard}>
                <div style={styles.infoHeader}>
                  <h3 style={styles.infoTitle}>Bill To:</h3>
                </div>
                {customerData ? (
                  <div style={styles.infoContent}>
                    <h4 style={styles.customerName}>{customerData.name}</h4>
                    {customerData.email && <p>Email: {customerData.email}</p>}
                    {customerData.phone && <p>Phone: {customerData.phone}</p>}
                    {customerData.address && (
                      <p>Address: {customerData.address}</p>
                    )}
                  </div>
                ) : (
                  <p>Customer information not available</p>
                )}
              </div>

              <div style={styles.infoCard}>
                <div style={styles.infoContent}>
                  <h4 style={styles.paymentHistoryTitle}>Payment Details</h4>
                  {cashierData && (
                    <div style={styles.cashierInfo}>
                      <p>
                        <strong>Cashier:</strong> {cashierData.name}
                      </p>
                    </div>
                  )}
                  
                  {/* Delivery Status for Online Orders - Hidden when printing */}
                  {isOnlineOrder && (
                    <div style={styles.deliveryStatusSection}>
                      <div style={styles.deliveryStatusRow}>
                        <FontAwesomeIcon 
                          icon={statusBadge.icon} 
                          style={{ color: statusBadge.color, marginRight: '8px' }} 
                        />
                        <strong>Delivery Status:</strong>
                        <span style={{
                          backgroundColor: statusBadge.bg,
                          color: statusBadge.color,
                          padding: '4px 12px',
                          borderRadius: '20px',
                          fontSize: '12px',
                          fontWeight: '600',
                          marginLeft: '8px',
                          textTransform: 'capitalize',
                        }}>
                          {statusBadge.label}
                        </span>
                      </div>
                      
                      {invoiceData?.attributes?.field_delivery_address && (
                        <div style={styles.deliveryAddressRow}>
                          <strong>Delivery Address:</strong>
                          <span>{safeString(invoiceData.attributes.field_delivery_address)}</span>
                        </div>
                      )}
                      
                      {/* Delivery Status Update Dropdown - Hidden when printing */}
                      <div style={styles.deliveryStatusUpdate} className="delivery-status-update">
                        <label style={styles.statusUpdateLabel}>Update Status:</label>
                        <select
                          value={deliveryStatus}
                          onChange={(e) => updateDeliveryStatus(e.target.value)}
                          style={styles.statusUpdateSelect}
                          disabled={updatingStatus}
                        >
                          {deliveryStatusOptions.map((option) => (
                            <option key={option.value} value={option.value}>
                              {option.label}
                            </option>
                          ))}
                        </select>
                        {updatingStatus && (
                          <FontAwesomeIcon icon={faSpinner} spin style={{ marginLeft: '8px' }} />
                        )}
                      </div>
                    </div>
                  )}

                  <button
                    style={styles.paymenthistorybtn}
                    className="payment-history-btn"
                    onClick={() => {
                      setShowPaymentHistoryModal(true);
                    }}
                  >
                    <FontAwesomeIcon icon={faHistory} /> Payment History
                  </button>
                </div>
              </div>
            </div>

            {/* ITEMS TABLE */}
            <div style={styles.itemsCard}>
              <div style={styles.tableContainer}>
                <table style={styles.itemsTable}>
                  <thead>
                    <tr>
                      <th style={styles.th}>#</th>
                      <th style={styles.th}>Item Name</th>
                      <th style={styles.th}>Quantity</th>
                      <th style={styles.th}>Unit Price</th>
                      <th style={styles.th}>Total</th>
                    </tr>
                  </thead>
                  <tbody>
                    {orderedproducts?.items?.length === 0 ? (
                      <tr>
                        <td colSpan="7" style={styles.noItems}>
                          No items found in this order
                        </td>
                      </tr>
                    ) : (
                      orderedproducts?.items?.map((item, index) => (
                        <tr
                          key={item.id || index}
                          style={
                            index % 2 === 0 ? styles.evenRow : styles.oddRow
                          }
                        >
                          <td style={styles.td}>{index + 1}</td>
                          <td style={styles.td}>
                            {safeString(item.product_title) || "N/A"}
                          </td>
                          <td style={styles.td}>{safeNumber(item.quantity)}</td>
                          <td style={styles.td}>
                            GHC
                            {safeNumber(item.unit_price).toFixed(2)}
                          </td>
                          <td style={styles.td}>GHC{safeNumber(item.line_total).toFixed(2)}</td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>

            {/* TOTALS */}
            <div style={styles.totalsCard}>
              <div style={styles.totalsGrid}>
                <div>
                  <div style={styles.totalsLeft}>
                    <h4 style={styles.notesTitle}>Notes</h4>
                    <p style={styles.notesText}>
                      {safeString(invoiceData.attributes.field_sales_order_notes) || "N/A"}
                    </p>
                  </div>

                  <div style={styles.termsCard}>
                    <h4 style={styles.termsTitle}>Terms and Conditions</h4>
                    <p style={styles.termsText}>
                      Information will be available soon.
                    </p>
                  </div>
                </div>

                <div style={styles.totalsRight}>
                  <div style={styles.orderSummarySection}>
                    <h4 style={styles.summaryTitle}>Summary</h4>

                    <div style={styles.totalRow}>
                      <span>Items Subtotal:</span>
                      <span>GHC{subtotal.toFixed(2)}</span>
                    </div>

                    {discount > 0 && (
                      <div style={styles.discountRow}>
                        <span style={{ color: "#ff9800", display: "flex", alignItems: "center", gap: "6px" }}>
                          Discount:
                        </span>
                        <span style={{ color: "#ff9800", fontWeight: "bold" }}>
                          - GHC{discount.toFixed(2)}
                        </span>
                      </div>
                    )}

                    <div style={styles.totalDivider}></div>

                    <div style={styles.grandTotalRow}>
                      <span><strong>Total:</strong></span>
                      <span><strong>GHC{total.toFixed(2)}</strong></span>
                    </div>

                    <div style={styles.paymentSummarySection}>
                      <div style={styles.totalRow}>
                        <span>Total Paid:</span>
                        <span style={styles.paidAmount}>GHC{totalPaid.toFixed(2)}</span>
                      </div>
                      <div style={styles.totalRow}>
                        <span>Balance Due:</span>
                        <span style={remainingBalance > 0 ? styles.balanceAmount : styles.paidAmount}>
                          GHC{remainingBalance.toFixed(2)}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* ACTION BUTTONS (Non-printable) */}
          <div style={styles.actionButtons} className="action-buttons-container">
            {remainingBalance > 0 && (
              <button
                style={{ ...styles.printButton, background: "#28a745" }}
                onClick={handleAddPayment}
              >
                <FontAwesomeIcon icon={faPlus} /> Add Payment
              </button>
            )}

            <button style={styles.printButton} onClick={handlePrint}>
              <FontAwesomeIcon icon={faPrint} /> A4 / PDF Invoice
            </button>

            <button
              style={{ ...styles.printButton, background: "#ff9800" }}
              onClick={printThermalReceiptDirectly}
              disabled={isPrinting}
            >
              <FontAwesomeIcon
                icon={isPrinting ? faSpinner : faReceipt}
                spin={isPrinting}
              />
              {isPrinting ? " Printing..." : " Print Thermal Receipt"}
            </button>

            <button style={styles.printButton} onClick={sendInvoiceViaWhatsApp}>
              <FontAwesomeIcon icon={faSms} /> WhatsApp Invoice
            </button>
            <button style={styles.printButton} onClick={handleEmail}>
              <FontAwesomeIcon icon={faEnvelope} /> Email Invoice
            </button>
            <button style={styles.printButton} onClick={handleSMS}>
              <FontAwesomeIcon icon={faMessage} /> SMS Invoice
            </button>
          </div>
        </div>
      </div>
      {/* <DashboardContentFooter /> */}

      {/* Payment Add Modal */}
      {showPaymentAddModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content">
            <div className="modal-body">
              <div className="modal-header">
                <p>Add Payment</p>
                <button
                  className="close-modal"
                  onClick={() => setShowPaymentAddModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
              <PaymentModal
                isOpen={showPaymentAddModal}
                onClose={() => setShowPaymentAddModal(false)}
                orderUuid={saleId}
                balance={remainingBalance}
                onSuccess={handlePaymentSuccess}
              />
            </div>
          </div>
        </div>
      )}

      {/* Payment History Modal */}
      {showPaymentHistoryModal && (
        <div className="modal">
          <div className="overlay"></div>
          <div className="modal-content">
            <div className="modal-body">
              <div className="modal-header">
                <p>Payment History</p>
                <button
                  className="close-modal"
                  onClick={() => setShowPaymentHistoryModal(false)}
                >
                  <FontAwesomeIcon icon={faClose} />
                </button>
              </div>
              <PaymentHistory invoiceorderedItems={invoiceorderedItems} />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// Styles
const styles = {
  container: {
    width: "100%",
    height: "100%",
    backgroundColor: "#ecf2fa",
    overflowX: "auto",
    WebkitOverflowScrolling: "touch",
    padding: "34px 32px 4rem 32px",
  },
  loadingContainer: {
    display: "flex",
    flexDirection: "column",
    alignItems: "center",
    justifyContent: "center",
    height: "400px",
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  loadingSpinner: {
    color: "#007bff",
    marginBottom: "20px",
  },
  loadingText: {
    color: "#666",
    fontSize: "16px",
  },
  errorContainer: {
    textAlign: "center",
    padding: "60px 20px",
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
  },
  errorIcon: {
    fontSize: "60px",
    color: "#dc3545",
    marginBottom: "20px",
  },
  backButton: {
    background: "#007bff",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "16px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    marginTop: "20px",
  },
  printContent: {
    background: "white",
    borderRadius: "12px",
    boxShadow: "0 4px 12px rgba(0,0,0,0.08)",
    padding: "30px",
    marginBottom: "20px",
  },
  invoiceHeader: {
    justifyContent: "center",
    marginBottom: "30px",
    paddingBottom: "12px",
    borderBottom: "2px solid #f0f0f0",
  },
  companyInfo: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  companyLogo: {
    background: "transparent",
    color: "white",
    width: "252px",
    height: "100px",
    borderRight: "1px solid #bdbbbb",
    marginRight: "12px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  companyContact: {
    display: "block",
    marginBottom: "16px",
  },
  companyContactText: {
    margin: "4px 0",
    fontSize: "15px",
    color: "#555",
    letterSpacing: "0.5px",
  },
  invoiceTitleContainer: {
    textAlign: "left",
  },
  invoiceTitle: {
    margin: "0 0 4px 40px",
    fontSize: "32px",
    fontWeight: "700",
    color: "#000001",
  },
  invoiceMeta: {
    display: "flex",
    flexDirection: "row",
    justifyContent: "center",
    alignItems: "center",
    gap: "32px",
  },
  metaItem: {
    display: "flex",
    gap: "6px",
  },
  metaLabel: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#666",
    textAlign: "right",
  },
  metaValue: {
    fontSize: "18px",
    fontWeight: "600",
    color: "#333",
  },
  onlineOrderBanner: {
    backgroundColor: "#e3f2fd",
    color: "#1976d2",
    padding: "10px 16px",
    borderRadius: "8px",
    marginBottom: "20px",
    display: "flex",
    alignItems: "center",
    gap: "10px",
    border: "1px solid #90caf9",
  },
  onlineOrderIcon: {
    fontSize: "18px",
  },
  onlineOrderText: {
    fontWeight: "700",
    fontSize: "16px",
  },
  onlineOrderSubtext: {
    fontSize: "14px",
  },
  infoGrid: {
    display: "grid",
    gridTemplateColumns: "repeat(2, 1fr)",
    gap: "20px",
    marginBottom: "20px",
  },
  infoCard: {
    background: "#f8f9fa",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  infoHeader: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginBottom: "15px",
  },
  infoIcon: {
    color: "#007bff",
    fontSize: "20px",
  },
  infoTitle: {
    margin: "0",
    fontSize: "18px",
    color: "#333",
  },
  infoContent: {
    color: "#555",
  },
  customerName: {
    margin: "0 0 10px 0",
    fontSize: "20px",
    color: "#333",
  },
  cashierInfo: {
    marginTop: "10px",
    paddingTop: "10px",
  },
  deliveryStatusSection: {
    marginTop: "10px",
    paddingTop: "10px",
    borderTop: "1px solid #e0e0e0",
  },
  deliveryStatusRow: {
    display: "flex",
    alignItems: "center",
    marginBottom: "8px",
  },
  deliveryAddressRow: {
    display: "flex",
    flexDirection: "column",
    marginBottom: "8px",
    fontSize: "14px",
  },
  deliveryStatusUpdate: {
    display: "flex",
    alignItems: "center",
    gap: "10px",
    marginTop: "8px",
  },
  statusUpdateLabel: {
    fontSize: "13px",
    fontWeight: "500",
    color: "#555",
  },
  statusUpdateSelect: {
    padding: "4px 8px",
    borderRadius: "4px",
    border: "1px solid #ddd",
    fontSize: "13px",
    background: "white",
    cursor: "pointer",
  },
  itemsCard: {
    marginBottom: "20px",
  },
  tableContainer: {
    overflowX: "auto",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  itemsTable: {
    width: "100%",
    borderCollapse: "collapse",
  },
  th: {
    background: "#333",
    padding: "12px",
    textAlign: "left",
    borderBottom: "2px solid #444",
    fontWeight: "normal",
    color: "#fff",
    whiteSpace: "nowrap",
  },
  td: {
    padding: "12px",
    borderBottom: "1px solid #e0e0e0",
    color: "#555",
    verticalAlign: "top",
  },
  noItems: {
    textAlign: "center",
    padding: "40px",
    color: "#666",
    fontStyle: "italic",
  },
  evenRow: {
    background: "#fafafa",
  },
  oddRow: {
    background: "white",
  },
  totalsCard: {
    background: "#f8f9fa",
    padding: "16px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
    marginBottom: "20px",
  },
  totalsGrid: {
    width: "100%",
    display: "flex",
    justifyContent: "space-between",
    gap: "40px",
  },
  totalsLeft: {
    paddingRight: "20px",
    flex: 1,
  },
  notesTitle: {
    margin: "0 0 15px 0",
    fontSize: "18px",
    color: "#333",
    fontWeight: "600",
  },
  notesText: {
    color: "#666",
    lineHeight: "1.6",
    fontSize: "14px",
    marginBottom: "20px",
  },
  totalsRight: {
    maxWidth: "400px",
  },
  totalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontWeight: "600",
    fontSize: "16px",
    color: "#666",
  },
  discountRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "8px 0",
    fontWeight: "bold",
    fontSize: "16px",
  },
  totalDivider: {
    height: "1px",
    background: "#e0e0e0",
    margin: "15px 0",
  },
  grandTotalRow: {
    display: "flex",
    justifyContent: "space-between",
    padding: "15px 0",
    fontSize: "18px",
    color: "#333",
    borderTop: "2px solid #333",
  },
  actionButtons: {
    display: "flex",
    gap: "15px",
    marginTop: "20px",
    flexWrap: "wrap",
  },
  printButton: {
    background: "#007bff",
    color: "white",
    border: "none",
    padding: "12px 24px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: "1 0 200px",
    minHeight: "48px",
    justifyContent: "center",
  },
  paymenthistorybtn: {
    background: "#C2C2C2",
    color: "black",
    border: "none",
    padding: "12px 18px",
    borderRadius: "8px",
    cursor: "pointer",
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    gap: "8px",
    flex: 1,
    marginTop: "16px",
  },
  termsCard: {
    background: "#f8f9fa",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  termsTitle: {
    margin: "0 0 10px 0",
    fontSize: "16px",
    color: "#333",
    fontWeight: "600",
  },
  termsText: {
    margin: "0",
    color: "#666",
    fontSize: "14px",
    lineHeight: "1.6",
  },
  orderSummarySection: {
    backgroundColor: "#fff",
    padding: "20px",
    borderRadius: "8px",
    border: "1px solid #e0e0e0",
  },
  summaryTitle: {
    margin: "0 0 15px 0",
    fontSize: "16px",
    color: "#333",
    fontWeight: "600",
    borderBottom: "2px solid #f0f0f0",
    paddingBottom: "8px",
  },
  paymentSummarySection: {
    paddingTop: "15px",
    borderTop: "2px solid #f0f0f0",
  },
  paidAmount: {
    fontWeight: "bold",
    color: "#28a745",
  },
  balanceAmount: {
    fontWeight: "bold",
    color: "#dc3545",
  },
  paymentHistoryTitle: {
    fontSize: "18px",
    fontWeight: "600",
    marginBottom: "10px",
    color: "#333",
  },
};

// Add print styles to hide non-printable elements
const printStyles = document.createElement("style");
printStyles.textContent = `
  @media screen {
    /* Hide header on screen */
    .print-only-header {
      display: none !important;
    }
  }

  @media print {
    /* Show header only when printing */
    .print-only-header {
      display: block !important;
    }
    
    /* Hide online order banner when printing */
    .online-order-banner {
      display: none !important;
    }
    
    /* Hide action buttons when printing */
    .action-buttons-container {
      display: none !important;
    }
    
    /* Hide delivery status update dropdown when printing */
    .delivery-status-update {
      display: none !important;
    }
    
    /* Hide payment history button when printing */
    .payment-history-btn {
      display: none !important;
    }
    
    /* Hide the TopActionMenus when printing */
    .top-action-menus {
      display: none !important;
    }
    
    /* Make sure the invoice content is clean */
    #invoice-print-content {
      margin: 0 !important;
      padding: 20px !important;
      box-shadow: none !important;
      border-radius: 0 !important;
      background: white !important;
    }
    
    /* Ensure the company logo prints properly */
    .company-logo img {
      max-width: 100% !important;
      height: auto !important;
    }
    
    /* Remove background colors for print */
    .online-order-banner {
      background-color: transparent !important;
      border: 1px solid #ccc !important;
    }
    
    /* ============ TABLE PRINT STYLES ============ */
    /* Remove table borders for cleaner print */
    .itemsTable {
      border-collapse: collapse !important;
      width: 100% !important;
    }
    
    .itemsTable th,
    .itemsTable td {
      border: none !important;
      padding: 8px 12px !important;
      text-align: left !important;
    }
    
    .itemsTable th {
      background-color: #333 !important;
      color: #fff !important;
      font-weight: bold !important;
    }
    
    /* Keep border between rows for readability */
    .itemsTable tr {
      border-bottom: 1px solid #e0e0e0 !important;
    }
    
    .itemsTable tr:last-child {
      border-bottom: none !important;
    }
    
    /* Remove alternating row colors */
    .evenRow,
    .oddRow {
      background-color: transparent !important;
    }
    
    /* Hide the table container border */
    .tableContainer {
      border: none !important;
      border-radius: 0 !important;
    }
    
    /* Summary section print styles */
    .totalsCard {
      border: none !important;
      background: transparent !important;
      padding: 10px 0 !important;
    }
    
    .totalRow,
    .discountRow,
    .grandTotalRow {
      border-bottom: none !important;
    }
    
    .grandTotalRow {
      border-top: 2px solid #333 !important;
    }
    
    /* Info cards print styles */
    .infoCard {
      border: 1px solid #ddd !important;
      background: transparent !important;
    }
    
    /* Payment summary section */
    .paymentSummarySection {
      border-top: 2px solid #ddd !important;
    }
  }
`;
document.head.appendChild(printStyles);